import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../api/countries/get-countries'
import { getPeriods } from '../../api/periods/get-periods'
import { getTransportMeans } from '../../api/transport-means/get-transport-means'
import { getTripPlaces } from '../../api/trip-places/get-trip-places'
import { TripTypeEnum, tripTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { TripFilters } from '../../lib/types/models/trip/trip'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

type Props = {
  filter?: TripFilters
  onFilterChange: (filter: TripFilters) => void
}

const TripFilter: React.FC<Props> = ({ filter, onFilterChange }) => {
  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  const { data: tripPlaces } = useQuery({
    queryKey: ['trip-places'],
    queryFn: () => getTripPlaces()
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Type de voyage</Typography>
        <div className="flex flex-row gap-5">
          <RadioInput
            checked={filter?.type === undefined}
            label={'Les deux'}
            onCheck={() => {
              onFilterChange({
                ...filter,
                type: undefined
              })
            }}
          />
          <RadioInput
            checked={filter?.type === TripTypeEnum.PRIMARY}
            label={tripTypeMapping[TripTypeEnum.PRIMARY].label}
            onCheck={() => {
              onFilterChange({
                ...filter,
                type: TripTypeEnum.PRIMARY
              })
            }}
          />
          <RadioInput
            checked={filter?.type === TripTypeEnum.SECONDARY}
            label={tripTypeMapping[TripTypeEnum.SECONDARY].label}
            onCheck={() => {
              onFilterChange({
                ...filter,
                type: TripTypeEnum.SECONDARY
              })
            }}
          />
        </div>
      </div>

      <MultiSelectInput
        searchable
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data.find((p) => p.name === period)?.name || '',
          value: period
        }))}
        variant="primary"
        onChange={(periods) => {
          onFilterChange({ ...filter, periods })
        }}
        options={
          periods?.data.map((period) => ({
            label: period.name,
            value: period.name
          })) || []
        }
        label="Périodes"
      />

      <MultiSelectInput
        searchable
        selectedOptions={filter?.countryCodes?.map((country) => ({
          label: countries?.data.find((c) => c.code === country)?.name || '',
          value: country
        }))}
        variant="primary"
        onChange={(countries) => {
          onFilterChange({ ...filter, countryCodes: countries })
        }}
        options={
          countries?.data.map((country) => ({
            label: country.name,
            value: country.code
          })) || []
        }
        label="Pays de séjour"
      />

      <MultiSelectInput
        searchable
        selectedOptions={filter?.transportMeans?.map((transportMean) => ({
          label: getTranslatedName(
            transportMeans?.data.find((c) => getTranslatedName(c) === transportMean)
          ),
          value: transportMean
        }))}
        variant="primary"
        onChange={(transportMeans) => {
          onFilterChange({ ...filter, transportMeans })
        }}
        options={
          transportMeans?.data.map((transportMean) => ({
            label: getTranslatedName(transportMean),
            value: getTranslatedName(transportMean)
          })) || []
        }
        label="Moyen de transport"
      />

      <MultiSelectInput
        searchable
        selectedOptions={filter?.departures?.map((tripPlace) => ({
          label: getTranslatedName(
            tripPlaces?.data.find((c) => getTranslatedName(c) === tripPlace)
          ),
          value: tripPlace
        }))}
        variant="primary"
        onChange={(departures) => {
          onFilterChange({ ...filter, departures })
        }}
        options={
          tripPlaces?.data.map((tripPlace) => ({
            label: getTranslatedName(tripPlace),
            value: getTranslatedName(tripPlace)
          })) || []
        }
        label="Lieu de départ"
      />

      <MultiSelectInput
        searchable
        selectedOptions={filter?.arrivals?.map((tripPlace) => ({
          label: getTranslatedName(
            tripPlaces?.data.find((c) => getTranslatedName(c) === tripPlace)
          ),
          value: tripPlace
        }))}
        variant="primary"
        onChange={(arrivals) => {
          onFilterChange({ ...filter, arrivals })
        }}
        options={
          tripPlaces?.data.map((tripPlace) => ({
            label: getTranslatedName(tripPlace),
            value: getTranslatedName(tripPlace)
          })) || []
        }
        label="Lieu d'arrivée"
      />
    </div>
  )
}

export default TripFilter
